import { useCallback, useEffect, useRef, useState } from 'react';

import { useAppStorage } from '@workspace/ui/hooks/useAppStorage';
import { useAuth } from '@workspace/ui/hooks/useAuth';

import type { ChatSettings } from '../types';

const SETTINGS_STORAGE_KEY = 'ai-assist-settings';

export const DEFAULT_SETTINGS: ChatSettings = {
  temperature: 0.7,
  maxTokens: -1,
  topP: 1,
  topK: 40,
  minP: 0,
  repeatPenalty: 1.1,
  presencePenalty: 0,
  frequencyPenalty: 0,
  repetitionPenalty: 1.0,
  seed: -1,
  taskProfileKey: null,
  customProfiles: [],
  personaId: null,
  defaultVoice: null,
  reasoningLevel: 'off',
  thinkingDisplayMode: 'collapsed',
  stop: [],
  disableLLM: false,
};

export function useSettings() {
  const { authenticated } = useAuth();
  const {
    data: remoteSettings,
    loading: remoteLoading,
    save: saveRemote,
  } = useAppStorage<ChatSettings>('chat', 'settings.json');

  const [settings, setSettings] = useState<ChatSettings>(DEFAULT_SETTINGS);
  const [isHydrated, setIsHydrated] = useState(false);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load settings: prefer remote (authenticated), fall back to localStorage
  useEffect(() => {
    if (remoteLoading) return;

    if (authenticated && remoteSettings) {
      setSettings({ ...DEFAULT_SETTINGS, ...remoteSettings });
      setIsHydrated(true);
      return;
    }

    // Fall back to localStorage
    if (typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem(SETTINGS_STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          setSettings({ ...DEFAULT_SETTINGS, ...parsed });
        }
      } catch (error) {
        console.error('Failed to load settings:', error);
      }
    }
    setIsHydrated(true);
  }, [authenticated, remoteSettings, remoteLoading]);

  // Cleanup timeout
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  // Persist helper: localStorage immediate + debounced remote
  const persist = useCallback(
    (newSettings: ChatSettings) => {
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(newSettings));
        } catch { /* localStorage full */ }
      }
      if (authenticated) {
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = setTimeout(() => {
          saveRemote(newSettings).catch((err) =>
            console.error('Failed to save settings to remote:', err),
          );
        }, 500);
      }
    },
    [authenticated, saveRemote],
  );

  // Save full settings
  const saveSettings = useCallback(
    (newSettings: ChatSettings) => {
      setSettings(newSettings);
      persist(newSettings);
    },
    [persist],
  );

  // Update a single setting
  const updateSetting = useCallback(
    <K extends keyof ChatSettings>(key: K, value: ChatSettings[K]) => {
      setSettings((prev) => {
        const newSettings = { ...prev, [key]: value };
        persist(newSettings);
        return newSettings;
      });
    },
    [persist],
  );

  // Update multiple settings at once (e.g. when applying a task profile)
  const batchUpdateSettings = useCallback(
    (changes: Partial<ChatSettings>) => {
      setSettings((prev) => {
        const newSettings = { ...prev, ...changes };
        persist(newSettings);
        return newSettings;
      });
    },
    [persist],
  );

  // Reset to defaults
  const resetSettings = useCallback(() => {
    saveSettings(DEFAULT_SETTINGS);
  }, [saveSettings]);

  return {
    settings,
    isHydrated,
    saveSettings,
    updateSetting,
    batchUpdateSettings,
    resetSettings,
  };
}
